import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import VisitTracker from "@/components/VisitTracker";
import SEOHead from "@/components/SEOHead";
import StickyCTA from "@/components/StickyCTA";
import MobileBottomNav from "@/components/MobileBottomNav";
import MatrixBackground from "@/components/MatrixBackground";
import ScrollToTop from "@/components/ScrollToTop";
import PageLoader from "@/components/PageLoader";
import TawkTo from "@/components/TawkTo";
import VersionWatcher from "@/components/VersionWatcher";
import ClickTracker from "@/components/ClickTracker";
import InstallPrompt from "@/components/InstallPrompt";
import HomePage from "@/pages/HomePage";
import { prefetchAllIdle } from "@/lib/prefetch";

/* All non-essential pages are code-split. The initial chunk now only
   contains HomePage + always-on chrome (Matrix, Toaster). Per-route
   bundles load on demand in < 100ms, then are cached.

   info/index.jsx is shared across many small pages — webpack will
   give it a single chunk because the named-export lazy() calls all
   import the same module. That means the FIRST info-page nav fetches
   the chunk, every subsequent info-page nav is instant. */
const ChallengesPage     = lazy(() => import(/* webpackPrefetch: true */ "@/pages/ChallengesPage"));
const AdminLoginPage     = lazy(() => import("@/pages/AdminLoginPage"));
const AdminDashboard     = lazy(() => import("@/pages/AdminDashboard"));
const GoogleCallback     = lazy(() => import("@/pages/GoogleCallback"));
const NotFoundPage       = lazy(() => import("@/pages/NotFoundPage"));
const LegalPage          = lazy(() => import("@/pages/LegalPage"));
const ComparePage        = lazy(() => import(/* webpackPrefetch: true */ "@/pages/ComparePage"));
const HowItWorksFullPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/HowItWorksFullPage"));
const ReviewsPage        = lazy(() => import("@/pages/ReviewsPage"));
const CalculatorFullPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/CalculatorFullPage"));
const ContactFullPage    = lazy(() => import("@/pages/ContactFullPage"));
const FAQFullPage        = lazy(() => import(/* webpackPrefetch: true */ "@/pages/FAQPage"));
const PartnerFullPage    = lazy(() => import("@/pages/PartnerFullPage"));
const SignaturePage      = lazy(() => import("@/pages/SignaturePage"));
const CompanyNewsPage    = lazy(() => import("@/pages/CompanyNewsPage"));
const CompanyNewsArticlePage = lazy(() => import("@/pages/CompanyNewsArticlePage"));

const info = (name) => lazy(() => import("@/pages/info").then((m) => ({ default: m[name] })));
const AboutPage              = info("AboutPage");
const GeneralRulesPage       = info("GeneralRulesPage");
const AffiliatesPage         = info("AffiliatesPage");
const PayoutProtectorPage    = info("PayoutProtectorPage");
const BlogPage               = info("BlogPage");
const CTraderPage            = info("CTraderPage");
const DXTradePage            = info("DXTradePage");
const MatchTraderPage        = info("MatchTraderPage");
const GooeyProPage           = info("GooeyProPage");
const WebTradingPage         = info("WebTradingPage");
const MobileTradingPage      = info("MobileTradingPage");
const AffiliateDashboardPage = info("AffiliateDashboardPage");
const InfluencersPage        = info("InfluencersPage");

function App() {
  // Eagerly warm every route chunk during browser idle time. By the
  // time the user clicks a menu item, the chunk is already cached and
  // navigation feels instant.
  if (typeof window !== "undefined") prefetchAllIdle();
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <MatrixBackground />
            <ScrollToTop />
            <VisitTracker />
            <SEOHead />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/challenges" element={<ChallengesPage />} />
                <Route path="/faq" element={<FAQFullPage />} />
                <Route path="/partner" element={<PartnerFullPage />} />
                <Route path="/how-it-works" element={<HowItWorksFullPage />} />
                <Route path="/general-rules" element={<GeneralRulesPage />} />
                <Route path="/affiliates" element={<AffiliatesPage />} />
                <Route path="/affiliates/influencers" element={<InfluencersPage />} />
                <Route path="/affiliates/dashboard" element={<AffiliateDashboardPage />} />
                <Route path="/payout-protector" element={<PayoutProtectorPage />} />
                <Route path="/calculator" element={<CalculatorFullPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/reviews" element={<ReviewsPage />} />
                <Route path="/contact" element={<ContactFullPage />} />
                <Route path="/platforms/compare" element={<ComparePage />} />
                <Route path="/platforms/ctrader" element={<CTraderPage />} />
                <Route path="/platforms/dxtrade" element={<DXTradePage />} />
                <Route path="/platforms/matchtrader" element={<MatchTraderPage />} />
                <Route path="/platforms/gooeypro" element={<GooeyProPage />} />
                <Route path="/platforms/web" element={<WebTradingPage />} />
                <Route path="/platforms/mobile" element={<MobileTradingPage />} />
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/auth/google" element={<GoogleCallback />} />
                <Route path="/legal/:slug" element={<LegalPage />} />
                <Route path="/signature/:slug" element={<SignaturePage />} />
                <Route path="/company-news" element={<CompanyNewsPage />} />
                <Route path="/company-news/:slug" element={<CompanyNewsArticlePage />} />
                {/* Catch-all 404 — must be LAST */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
            <StickyCTA />
            <MobileBottomNav />
            <TawkTo />
            <VersionWatcher />
            <ClickTracker />
            <InstallPrompt />
            <Toaster theme="dark" position="bottom-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
